import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { useFlashToast } from '../../hooks/useFlashToast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUser, faLock, faEye, faEyeSlash, faFloppyDisk, faCommentDots, faLocationDot, faShieldHalved, faCrown
} from '@fortawesome/free-solid-svg-icons'
import UserSidebar from '../../components/user/Sidebar'
import UserTopbar from '../../components/user/Topbar'
import AdminSidebar from '../../components/admin/Sidebar'
import AdminTopbar from '../../components/admin/Topbar'
import SuperadminSidebar from '../../components/superadmin/Sidebar'
import SuperadminTopbar from '../../components/superadmin/Topbar'

const SHELL = {
  user: { Sidebar: UserSidebar, Topbar: UserTopbar, bg: 'bg-gray-50', accent: 'green' },
  admin: { Sidebar: AdminSidebar, Topbar: AdminTopbar, bg: 'bg-gray-50', accent: 'blue' },
  superadmin: { Sidebar: SuperadminSidebar, Topbar: SuperadminTopbar, bg: 'bg-gray-950', accent: 'indigo' },
}

function PasswordField({ label, ...props }) {
  const [show, setShow] = useState(false)
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
      <div className="relative">
        <FontAwesomeIcon icon={faLock} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input type={show ? 'text' : 'password'} className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" {...props} />
        <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          <FontAwesomeIcon icon={show ? faEyeSlash : faEye} className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

export default function StaffSettings({ current_user, portal = 'admin', settings_path }) {
  useFlashToast()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { Sidebar, Topbar, bg } = SHELL[portal] || SHELL.admin
  const dark = portal === 'superadmin'

  const profile = useForm({
    update_type: 'profile',
    name: current_user?.name || '',
    address: current_user?.address || '',
    whatsapp_number: current_user?.whatsapp_number || '',
  })

  const password = useForm({
    update_type: 'password',
    current_password: '',
    password: '',
    password_confirmation: '',
  })

  const card = dark ? 'bg-gray-900 border-gray-800 text-gray-100' : 'bg-white border-gray-200'
  const initials = (current_user?.name || current_user?.username || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className={`flex h-screen font-sans overflow-hidden ${bg}`}>
      <Sidebar current_user={current_user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar current_user={current_user} onMenuToggle={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="mb-6">
            <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>Pengaturan Akun</h1>
            <p className={`text-sm mt-0.5 ${dark ? 'text-gray-500' : 'text-gray-500'}`}>Kelola profil dan keamanan akun staff</p>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            <div className={`rounded-xl border overflow-hidden ${card}`}>
              <div className={`h-2 ${dark ? 'bg-gradient-to-r from-indigo-600 to-violet-500' : 'bg-gradient-to-r from-blue-500 to-indigo-400'}`} />
              <div className="p-6 flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold ${dark ? 'bg-indigo-900/60 text-indigo-300 ring-1 ring-indigo-600/40' : 'bg-blue-100 text-blue-700'}`}>
                  {portal === 'superadmin' ? <FontAwesomeIcon icon={faCrown} className="w-6 h-6" /> : initials}
                </div>
                <div>
                  <p className={`font-semibold ${dark ? 'text-white' : 'text-gray-900'}`}>{current_user?.name || current_user?.username}</p>
                  <p className={`text-sm ${dark ? 'text-gray-500' : 'text-gray-500'}`}>{current_user?.email}</p>
                  <span className={`inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${dark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                    <FontAwesomeIcon icon={faShieldHalved} className="w-2.5 h-2.5" />
                    {portal === 'superadmin' ? 'Superadmin' : 'Admin'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <form onSubmit={(e) => { e.preventDefault(); profile.patch(settings_path) }} className={`rounded-xl border p-6 space-y-4 ${card}`}>
                <h2 className={`text-sm font-semibold ${dark ? 'text-gray-200' : 'text-gray-800'}`}>Edit Profil</h2>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Nama</label>
                  <input value={profile.data.name} onChange={e => profile.setData('name', e.target.value)} className={`w-full px-3 py-2.5 text-sm rounded-lg border outline-none ${dark ? 'bg-gray-800 border-gray-700 text-gray-100' : 'border-gray-300'}`} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">WhatsApp</label>
                  <input value={profile.data.whatsapp_number} onChange={e => profile.setData('whatsapp_number', e.target.value)} className={`w-full px-3 py-2.5 text-sm rounded-lg border outline-none ${dark ? 'bg-gray-800 border-gray-700 text-gray-100' : 'border-gray-300'}`} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5">Alamat</label>
                  <textarea rows={3} value={profile.data.address} onChange={e => profile.setData('address', e.target.value)} className={`w-full px-3 py-2.5 text-sm rounded-lg border outline-none resize-none ${dark ? 'bg-gray-800 border-gray-700 text-gray-100' : 'border-gray-300'}`} />
                </div>
                <button type="submit" disabled={profile.processing} className={`w-full py-2.5 text-sm font-semibold text-white rounded-lg ${dark ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  <FontAwesomeIcon icon={faFloppyDisk} className="w-3.5 h-3.5 mr-2" />
                  Simpan Profil
                </button>
              </form>

              <form onSubmit={(e) => { e.preventDefault(); password.patch(settings_path, { onSuccess: () => password.reset() }) }} className={`rounded-xl border p-6 space-y-4 ${card}`}>
                <h2 className={`text-sm font-semibold ${dark ? 'text-gray-200' : 'text-gray-800'}`}>Ubah Password</h2>
                <PasswordField label="Password Saat Ini" value={password.data.current_password} onChange={e => password.setData('current_password', e.target.value)} autoComplete="current-password" />
                <PasswordField label="Password Baru" value={password.data.password} onChange={e => password.setData('password', e.target.value)} autoComplete="new-password" />
                <PasswordField label="Konfirmasi" value={password.data.password_confirmation} onChange={e => password.setData('password_confirmation', e.target.value)} autoComplete="new-password" />
                <button type="submit" disabled={password.processing} className="w-full py-2.5 text-sm font-semibold text-white rounded-lg bg-amber-500 hover:bg-amber-600">
                  Perbarui Password
                </button>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
